import React, { useState, useEffect } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import { medicineAPI } from '../../../utils/api';

const Products = () => {
  const { showSuccess, showError } = useNotification();
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [cartCount, setCartCount] = useState(0);
  const navigate = require('react-router-dom').useNavigate();

  useEffect(() => {
    fetchProducts();
    updateCartCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateCartCount = () => {
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    setCartCount(cart.reduce((acc, item) => acc + item.quantity, 0));
  };

  const fetchProducts = async () => {
    try {
      const res = await medicineAPI.getApprovedMedicines();
      if (res.data?.success) {
        setMedicines(res.data.data || []);
      }
    } catch (error) {
      showError('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const filteredMedicines = medicines.filter(medicine =>
    medicine.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    medicine.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const addToCart = (medicine) => {
    try {
      const cart = JSON.parse(localStorage.getItem('cart') || '[]');
      const existingItem = cart.find(item => item.medicine_id === medicine._id);

      if (existingItem) {
        existingItem.quantity += 1;
      } else {
        cart.push({
          medicine_id: medicine._id,
          name: medicine.name,
          price: medicine.price,
          quantity: 1,
          retailer_id: medicine.retailer_id?._id || medicine.retailer_id // Ensure retailer ID is captured
        });
      }

      localStorage.setItem('cart', JSON.stringify(cart));
      updateCartCount();
      showSuccess('Added to cart successfully');
    } catch (error) {
      console.error(error);
      showError('Failed to add to cart');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading medicines...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gold">Products</h1>
        <div className="flex space-x-2">
          <button
            onClick={() => navigate('/patient/orders')}
            className="bg-gray-700 text-light px-4 py-2 rounded-lg font-medium hover:bg-gray-600 transition"
          >
            My Orders
          </button>
          <button
            onClick={() => navigate('/patient/cart')}
            className="bg-gold text-dark px-4 py-2 rounded-lg font-medium hover:opacity-90 transition"
          >
            Cart ({cartCount})
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <input
          type="text"
          placeholder="Search products..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-gray-800 rounded-xl py-3 px-4 pl-12 text-light placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold"
        />
        <svg className="w-5 h-5 text-gray-400 absolute left-4 top-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredMedicines.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
            </div>
            <p className="text-gray-400">No products found</p>
            {searchTerm && <p className="text-sm text-gray-500 mt-2">Try different search terms</p>}
          </div>
        ) : (
          filteredMedicines.map((medicine) => (
            <div key={medicine._id} className="bg-gray-800 rounded-xl p-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-lg">{medicine.name}</h3>
                  <p className="text-gray-400 text-sm mt-1">{medicine.description}</p>
                </div>
                {medicine.is_approved && (
                  <span className="bg-green-900 text-green-300 px-2 py-1 rounded-full text-xs">
                    Approved
                  </span>
                )}
              </div>

              <div className="flex justify-between items-center">
                <div>
                  <p className="text-gold font-bold text-lg">₹{medicine.price}</p>
                  <p className="text-gray-400 text-sm">Stock: {medicine.stock_quantity || '0'}</p>
                </div>
                <button
                  onClick={() => addToCart(medicine)}
                  disabled={!medicine.stock_quantity || medicine.stock_quantity <= 0}
                  className={`px-4 py-2 rounded-lg font-medium ${!medicine.stock_quantity || medicine.stock_quantity <= 0
                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    : 'bg-gold text-dark hover:opacity-90'
                    }`}
                >
                  {(!medicine.stock_quantity || medicine.stock_quantity <= 0) ? 'Out of Stock' : 'Add to Cart'}
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Products;
